/**
 * useStrategyPlatformCadence — convenience wrapper for reading + writing
 * strategy.platformCadences. Keeps the PlatformCadenceEditor dialog
 * independent of ContentStrategyFirestoreService internals.
 */

import { useCallback, useMemo } from 'react';
import { ContentStrategyFirestoreService } from '../services/ContentStrategyFirestoreService';
import type { CalendarAwareContentStrategy, StrategyPlatformCadence } from '../types';

export interface UseStrategyPlatformCadenceReturn {
  cadences: StrategyPlatformCadence[];
  saveCadences: (next: StrategyPlatformCadence[]) => Promise<void>;
}

export function useStrategyPlatformCadence(
  projectId: string | null,
  strategy: CalendarAwareContentStrategy | null,
): UseStrategyPlatformCadenceReturn {
  const service = useMemo(() => new ContentStrategyFirestoreService(), []);
  const cadences = strategy?.platformCadences ?? [];

  const saveCadences = useCallback(
    async (next: StrategyPlatformCadence[]) => {
      if (!projectId || !strategy) {
        throw new Error('useStrategyPlatformCadence.saveCadences: missing projectId or strategy');
      }
      await service.updatePlatformCadences(projectId, strategy.id, next);
    },
    [projectId, strategy, service],
  );

  return { cadences, saveCadences };
}
